import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import Iconify from 'src/components/Iconify';
import ListActionBar from 'src/components/ListActionBar';
import TableActionButton from 'src/components/TableActionButton';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { useDeploymentRings } from 'src/hooks/deploymentRing';
import AddDeploymentRingModal from 'src/features/deploymentRing/AddDeploymentRingModal';
import { getDeploymentRingDetailsRoute } from 'src/pages/deploymentRingDetails/constants';
import DeploymentRingsTable from './DeploymentRingsTable';

const RING_STATUS = { Active: 'active', Inactive: 'inactive' };

// Inactive rings start out hidden. The filter lives in the URL so it survives opening a ring and
// coming back. As on the devices list, no status ticked means no narrowing at all.
const DEFAULT_FILTERS = { statuses: [RING_STATUS.Active] };

const FILTER_OPTIONS = [
  {
    key: 'statuses',
    type: 'checkbox',
    label: 'Ring Status',
    options: [
      { value: RING_STATUS.Active, label: 'Active' },
      { value: RING_STATUS.Inactive, label: 'Inactive' },
    ],
  },
];

const DeploymentRingsList = () => {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [filters, setFilters] = useState(
    () => readFilterParams(DEFAULT_FILTERS, FILTER_OPTIONS) ?? DEFAULT_FILTERS,
  );

  useFilterParams(filters);

  const ringsQuery = useDeploymentRings();

  const rings = useMemo(
    () =>
      (ringsQuery.data?.rings || []).filter(
        ring =>
          !filters.statuses.length ||
          filters.statuses.includes(ring.isActive ? RING_STATUS.Active : RING_STATUS.Inactive),
      ),
    [ringsQuery.data, filters.statuses],
  );

  const handleFilterChange = useCallback(
    nextFilters => setFilters({ statuses: nextFilters.statuses || [] }),
    [],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={FILTER_OPTIONS}
        filterChips
        actionArea={
          <TableActionButton
            variant="outlined"
            onClick={() => setAdding(true)}
            startIcon={<Iconify icon="mdi:plus" width={16} height={16} />}
            sx={{ px: 1.5 }}>
            Add Deployment Ring
          </TableActionButton>
        }
      />

      <DeploymentRingsTable
        rings={rings}
        isLoading={ringsQuery.isLoading}
        onOpen={ring => navigate(getDeploymentRingDetailsRoute(ring.id))}
      />

      {adding ? <AddDeploymentRingModal onClose={() => setAdding(false)} /> : null}
    </Box>
  );
};

export default DeploymentRingsList;
